import { z } from 'zod';

import { refetchCitationRequests } from '../api/citationRequestCache';
import { CITATION_REQUESTS_ROOT_KEY, STATEMENT_REFERENCES_ROOT_KEY } from '../api/queryOptions';
import { refetchStatementReferences } from '../api/statementReferenceCache';

import { defineFeatureRealtimeHandler } from '@/hooks/realtime/featureRealtime';

const conversationData = z.object({ conversationId: z.string().min(1) });

/**
 * The server's `referenceAssistant.*` hints. Fields beyond what the client
 * acts on are left out, so the server can add one without the client
 * dropping the event.
 */
const referenceAssistantEventSchema = z.discriminatedUnion('type', [
  z.object({
    v: z.literal(1),
    type: z.literal('referenceAssistant.message.updated'),
    data: conversationData.extend({ messageSid: z.string().min(1) }),
  }),
  z.object({
    v: z.literal(1),
    type: z.literal('referenceAssistant.citationRequest.updated'),
    data: conversationData.extend({ citationRequestId: z.string().min(1) }),
  }),
]);

export type ReferenceAssistantRealtimeEvent = z.infer<typeof referenceAssistantEventSchema>;

/**
 * Re-reads what a hint names: a message's statements and references, or a
 * conversation's citation requests. Either list is fetched only if a screen
 * is showing it; otherwise it is marked stale for the next one that does.
 *
 * On resume it re-reads both everywhere a screen shows them, because a hint
 * sent while the socket was down is never replayed.
 */
export const referenceAssistantRealtimeHandler = defineFeatureRealtimeHandler({
  prefix: 'referenceAssistant',
  parse: (raw) => referenceAssistantEventSchema.parse(raw),
  apply: (queryClient, event) => {
    if (event.type === 'referenceAssistant.message.updated') {
      refetchStatementReferences(queryClient, event.data.conversationId).catch(() => undefined);

      return;
    }

    refetchCitationRequests(queryClient, event.data.conversationId).catch(() => undefined);
  },
  onResume: (queryClient) => {
    for (const queryKey of [STATEMENT_REFERENCES_ROOT_KEY, CITATION_REQUESTS_ROOT_KEY]) {
      queryClient.invalidateQueries({ queryKey, refetchType: 'active' }).catch(() => undefined);
    }
  },
});
